/**
 * PhotoStackScene — Animated photo stack for the "Scanning Media" stage.
 */
import { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { BRAND } from '../../config'
import type { PhotosSceneData } from '../../types'

// ── Helpers ───────────────────────────────────────────────────────────────────

function stackRotation(index: number): number {
  const angles = [-3.5, 2.8, -1.8, 4.2, -2.5, 3.1, -4, 1.5]
  return angles[index % angles.length]
}

function stackOffset(index: number): { x: number; y: number } {
  const offsets = [
    { x: 0, y: 0 }, { x: 6, y: 6 }, { x: -4, y: 4 },
    { x: 8, y: 2 }, { x: -6, y: 8 }, { x: 4, y: -2 },
  ]
  return offsets[index % offsets.length]
}

const CATEGORY_COLORS: Record<string, string> = {
  exterior:  '#3b82f6',
  interior:  '#8b5cf6',
  food:      '#f97316',
  logo:      '#6366f1',
  menu:      '#10b981',
  product:   '#f59e0b',
  team:      '#ec4899',
  at_work:   '#14b8a6',
  by_owner:  '#01114f',
  customers: '#ef4444',
}

function categoryColor(cat: string): string {
  return CATEGORY_COLORS[cat.toLowerCase()] ?? BRAND.navy
}

function categoryLabel(cat: string): string {
  if (!cat) return 'Photo'
  return cat.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

// ── Main component ─────────────────────────────────────────────────────────────

interface PhotoStackSceneProps {
  data: PhotosSceneData | null
}

export function PhotoStackScene({ data }: PhotoStackSceneProps) {
  const { t } = useTranslation('scan')
  const photos = data?.photos ?? []
  const totalCount = data?.total ?? 0

  const [visibleCount, setVisibleCount] = useState(0)

  useEffect(() => {
    if (photos.length === 0) return
    setVisibleCount(0)
    const timer = setInterval(() => {
      setVisibleCount(prev => {
        if (prev >= photos.length) {
          clearInterval(timer)
          return prev
        }
        return prev + 1
      })
    }, 600)
    return () => clearInterval(timer)
  }, [photos.length])

  if (photos.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4 text-gray-400">
        <motion.div
          animate={{ scale: [1, 1.08, 1] }}
          transition={{ repeat: Infinity, duration: 1.8 }}
          className="text-5xl"
        >
          📸
        </motion.div>
        <p className="text-sm font-medium">{t('scene.photos.fetching')}</p>
        <div className="flex gap-1.5">
          {[0, 1, 2].map(i => (
            <motion.span
              key={i}
              className="w-2 h-2 rounded-full bg-gray-300"
              animate={{ scale: [1, 1.4, 1] }}
              transition={{ repeat: Infinity, delay: i * 0.2, duration: 0.7 }}
            />
          ))}
        </div>
      </div>
    )
  }

  const visiblePhotos = photos.slice(0, visibleCount)
  const stackPhotos = visiblePhotos.slice(-8)

  return (
    <div className="flex flex-col items-center justify-center h-full gap-6">
      {/* Counter badge */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center gap-3"
      >
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
          {t('scene.photos.found')}
        </span>
        <motion.span
          key={visibleCount}
          initial={{ scale: 1.3, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 300 }}
          className="text-3xl font-bold"
          style={{ color: BRAND.navy }}
        >
          {visibleCount}
        </motion.span>
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
          {t('scene.photos.ofTotal', { total: totalCount })}
        </span>
      </motion.div>

      {/* Photo stack */}
      <div className="relative" style={{ width: 240, height: 170 }}>
        <AnimatePresence>
          {stackPhotos.map((photo, i) => {
            const globalIndex = visiblePhotos.length - stackPhotos.length + i
            const isTop = i === stackPhotos.length - 1
            const rotation = stackRotation(globalIndex)
            const offset = stackOffset(i)
            const zIndex = i + 1

            return (
              <motion.div
                key={photo.url}
                initial={{ y: 60, opacity: 0, scale: 0.85, rotate: rotation * 2 }}
                animate={{
                  y: offset.y,
                  x: offset.x,
                  opacity: 1,
                  scale: isTop ? 1 : 0.96 - (stackPhotos.length - 1 - i) * 0.02,
                  rotate: rotation,
                }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ type: 'spring', stiffness: 200, damping: 20 }}
                className="absolute inset-0 rounded-2xl overflow-hidden shadow-lg"
                style={{ zIndex, border: '3px solid white' }}
              >
                <img
                  src={photo.thumbnail || photo.url}
                  alt={photo.category}
                  className="w-full h-full object-cover"
                  onError={e => {
                    (e.target as HTMLImageElement).src =
                      `https://ui-avatars.com/api/?name=${encodeURIComponent(photo.category)}&size=240&background=f3f4f6&color=6b7280`
                  }}
                />

                {isTop && photo.category && (
                  <motion.div
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15 }}
                    className="absolute bottom-2 left-2"
                  >
                    <span
                      className="text-xs font-semibold text-white px-2.5 py-1 rounded-full"
                      style={{ background: categoryColor(photo.category) }}
                    >
                      {categoryLabel(photo.category)}
                    </span>
                  </motion.div>
                )}
              </motion.div>
            )
          })}
        </AnimatePresence>

        {stackPhotos.length === 0 && (
          <div className="absolute inset-0 rounded-2xl border-2 border-dashed border-gray-200 flex items-center justify-center">
            <span className="text-3xl">🖼️</span>
          </div>
        )}
      </div>

      {/* Category legend */}
      {visiblePhotos.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="flex flex-wrap justify-center gap-1.5 max-w-xs"
        >
          {Array.from(new Set(visiblePhotos.map(p => p.category).filter(Boolean))).map(cat => (
            <span
              key={cat}
              className="text-xs font-medium px-2.5 py-0.5 rounded-full text-white"
              style={{ background: categoryColor(cat) }}
            >
              {categoryLabel(cat)}
            </span>
          ))}
        </motion.div>
      )}

      {/* Progress bar */}
      {totalCount > 0 && (
        <div className="w-48">
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <motion.div
              className="h-full rounded-full"
              style={{ background: `linear-gradient(90deg, ${BRAND.navy}, ${BRAND.red})` }}
              animate={{ width: `${Math.min((visibleCount / Math.min(totalCount, photos.length)) * 100, 100)}%` }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
            />
          </div>
          <p className="text-xs text-center text-gray-400 mt-1.5">
            {t('scene.photos.analyzing')}
          </p>
        </div>
      )}
    </div>
  )
}
